import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { useI18n } from '../context/I18nContext';
import axios from 'axios';
import { Monitor, Smartphone, Shield, Package, RefreshCw } from 'lucide-react';
import { API } from '../config';

const deviceIcons = { windows: Monitor, macos: Monitor, linux: Monitor, ios: Smartphone, android: Smartphone, mobile: Smartphone, unknown: Monitor };

const labels = {
  fr: { title: 'Mes licences', loading: 'Chargement des licences...', none: "Aucune licence. Achetez un forfait pour commencer.", devices: 'appareil(s) actif(s)', noDevice: "Aucun appareil activé. Ouvrez HexoPDF pour l'activer.", unknown: 'Appareil inconnu' },
  en: { title: 'My Licenses', loading: 'Loading licenses...', none: 'No licenses yet. Purchase a plan to get started.', devices: 'device(s) active', noDevice: 'No device activated yet. Open HexoPDF to activate.', unknown: 'Unknown device' },
  es: { title: 'Mis licencias', loading: 'Cargando licencias...', none: 'Sin licencias. Compra un plan para empezar.', devices: 'dispositivo(s) activo(s)', noDevice: 'Ningún dispositivo activado. Abre HexoPDF para activar.', unknown: 'Dispositivo desconocido' },
  de: { title: 'Meine Lizenzen', loading: 'Lizenzen laden...', none: 'Noch keine Lizenzen. Kaufen Sie einen Plan.', devices: 'Gerät(e) aktiv', noDevice: 'Kein Gerät aktiviert. Öffnen Sie HexoPDF.', unknown: 'Unbekanntes Gerät' },
  it: { title: 'Le mie licenze', loading: 'Caricamento licenze...', none: 'Nessuna licenza. Acquista un piano.', devices: 'dispositivo/i attivo/i', noDevice: 'Nessun dispositivo attivato. Apri HexoPDF.', unknown: 'Dispositivo sconosciuto' },
  pt: { title: 'Minhas licenças', loading: 'Carregando licenças...', none: 'Sem licenças. Compre um plano.', devices: 'dispositivo(s) ativo(s)', noDevice: 'Nenhum dispositivo ativado. Abra o HexoPDF.', unknown: 'Dispositivo desconhecido' },
  nl: { title: 'Mijn licenties', loading: 'Licenties laden...', none: 'Geen licenties. Koop een plan.', devices: 'apparaat/apparaten actief', noDevice: 'Geen apparaat geactiveerd. Open HexoPDF.', unknown: 'Onbekend apparaat' },
  pl: { title: 'Moje licencje', loading: 'Ładowanie licencji...', none: 'Brak licencji. Kup plan.', devices: 'aktywne urządzenia', noDevice: 'Brak aktywnych urządzeń. Otwórz HexoPDF.', unknown: 'Nieznane urządzenie' },
  tr: { title: 'Lisanslarım', loading: 'Lisanslar yükleniyor...', none: 'Lisans yok. Bir plan satın alın.', devices: 'cihaz aktif', noDevice: 'Etkinleştirilmiş cihaz yok. HexoPDF açın.', unknown: 'Bilinmeyen cihaz' },
  ru: { title: 'Мои лицензии', loading: 'Загрузка лицензий...', none: 'Нет лицензий. Приобретите план.', devices: 'устройств(а) активно', noDevice: 'Устройств не активировано. Откройте HexoPDF.', unknown: 'Неизвестное устройство' },
  ja: { title: 'マイライセンス', loading: 'ライセンスを読み込み中...', none: 'ライセンスがありません。プランを購入してください。', devices: 'デバイスがアクティブ', noDevice: 'デバイスが未アクティブ。HexoPDFを開いてください。', unknown: '不明なデバイス' },
  zh: { title: '我的许可证', loading: '加载许可证...', none: '暂无许可证。请购买计划。', devices: '设备已激活', noDevice: '尚未激活设备。请打开HexoPDF。', unknown: '未知设备' },
  ar: { title: 'تراخيصي', loading: 'جاري تحميل التراخيص...', none: 'لا توجد تراخيص. اشترِ خطة.', devices: 'جهاز/أجهزة نشطة', noDevice: 'لم يتم تفعيل أي جهاز. افتح HexoPDF.', unknown: 'جهاز غير معروف' },
  hi: { title: 'मेरे लाइसेंस', loading: 'लाइसेंस लोड हो रहे हैं...', none: 'अभी कोई लाइसेंस नहीं। शुरू करने के लिए एक प्लान खरीदें।', devices: 'डिवाइस सक्रिय', noDevice: 'कोई डिवाइस सक्रिय नहीं। HexoPDF खोलें।', unknown: 'अज्ञात डिवाइस' },
};

const editionLabels = { standard: 'Standard', premium: 'Premium', founder: 'Founder' };
const platformLabels = { desktop: 'Desktop', mobile: 'Mobile' };

export const LicenseSection = () => {
  const { getToken } = useAuth();
  const { lang } = useI18n();
  const l = labels[lang] || labels.en;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const headers = { Authorization: `Bearer ${getToken()}` };

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/license/status`, { headers })
      .then(r => setData(r.data))
      .catch(() => setData({ entitlements: [], activations: [] }))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return (
      <div className="p-6 rounded-2xl bg-card border border-border" data-testid="license-section-loading">
        <div className="flex items-center gap-2 text-muted-foreground text-sm">
          <RefreshCw className="w-4 h-4 animate-spin" /> {l.loading}
        </div>
      </div>
    );
  }

  const entitlements = data?.entitlements || [];
  const activations = data?.activations || [];

  return (
    <div className="space-y-4" data-testid="license-section">
      <div className="p-6 rounded-2xl bg-card border border-border">
        <h2 className="text-sm uppercase tracking-[0.15em] text-muted-foreground font-medium mb-4 flex items-center gap-2">
          <Package className="w-4 h-4" /> {l.title}
        </h2>

        {entitlements.length === 0 ? (
          <p className="text-muted-foreground text-sm" data-testid="license-no-entitlements">
            {l.none}
          </p>
        ) : (
          <div className="space-y-3">
            {entitlements.map((ent) => {
              const entActivations = activations.filter(a => a.entitlement_id === ent.id);
              const edition = editionLabels[ent.edition] || ent.edition || '';
              const platforms = (ent.platforms || []).map(p => platformLabels[p] || p).join(' + ');
              const deviceLimit = ent.device_limit || ent.max_activations || 1;
              const displayName = ent.metadata?.package_name || `HexoPDF ${edition} ${platforms}`.trim();

              return (
                <div key={ent.id} className="p-4 rounded-xl bg-secondary/50 border border-border/50" data-testid={`entitlement-${ent.id}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center gap-2">
                      <Shield className="w-4 h-4 text-blue-500" />
                      <span className="text-foreground font-medium text-sm">{displayName}</span>
                    </div>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${
                      ent.status === 'active' ? 'bg-emerald-500/10 text-emerald-400' : 'bg-red-500/10 text-red-400'
                    }`}>
                      {ent.status}
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground mb-1">
                    {edition && <span className="mr-2">{edition}</span>}
                    {platforms && <span className="mr-2">{platforms}</span>}
                    {ent.channel && <span className="text-blue-400/70">({ent.channel})</span>}
                  </div>
                  <div className="text-xs text-muted-foreground mb-3">
                    {entActivations.length}/{deviceLimit} {l.devices}
                  </div>

                  {entActivations.length > 0 ? (
                    <div className="space-y-2">
                      {entActivations.map((act) => {
                        const DevIcon = deviceIcons[act.device_type] || Monitor;
                        return (
                          <div key={act.id} className="flex items-center p-2.5 rounded-lg bg-background" data-testid={`activation-${act.id}`}>
                            <div className="flex items-center gap-2.5">
                              <DevIcon className="w-4 h-4 text-muted-foreground" />
                              <div>
                                <p className="text-foreground text-sm">{act.device_name || l.unknown}</p>
                                <p className="text-muted-foreground text-xs">
                                  {act.device_type} · {act.platform || ''} · {act.app_version}
                                </p>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  ) : (
                    <p className="text-muted-foreground/60 text-xs italic">
                      {l.noDevice}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};
